/**
 * The sections of Jenny's weekly report read from the location's ads snapshot (backend
 * modules/ads/snapshot, passed through by agent-suite adsReportView.ts as `report.snapshot`).
 * Only the fields the page shows are read; every figure is the snapshot's own, never recomputed
 * here. A section the snapshot marks unavailable carries its reason, never zeros.
 */

type Json = Record<string, unknown>;

const isRecord = (v: unknown): v is Json =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const rec = (v: unknown): Json => (isRecord(v) ? v : {});
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

/** A section either holds its figures or says why it can't. */
export type Section<T> = { available: true; value: T } | { available: false; reason: string };

function section<T>(raw: unknown, read: (r: Json) => T): Section<T> {
  const r = rec(raw);
  if (r.available === false) return { available: false, reason: str(r.reason) ?? 'not available' };
  if (r.available !== true) return { available: false, reason: 'not in this report' };
  return { available: true, value: read(r) };
}

/** A snapshot table (`columns` + `rows`) as one object per row. */
export function tableRows(raw: unknown): Json[] {
  const t = rec(raw);
  if (!Array.isArray(t.columns) || !Array.isArray(t.rows)) return [];
  const columns = t.columns.map((c) => (typeof c === 'string' ? c : ''));
  return t.rows.flatMap((row) =>
    Array.isArray(row) ? [Object.fromEntries(columns.map((c, i) => [c, row[i]]))] : [],
  );
}

export interface WeekFigures {
  spend: number | null;
  calls: number | null;
  callsAtLeast30s: number | null;
  missed: number | null;
}
export interface ShareFigures {
  impressionSharePercent: number | null;
  lostToBudgetPercent: number | null;
  lostToRankPercent: number | null;
  includesUnderTenPercent: boolean;
  /** Google gave the shown share only as "under 10%": it reads "under 10%", never "10%". */
  shownUnderTen: boolean;
}
/** Top-of-page: shown above the organic results (top) and in the very first position (absolute top). */
export interface TopShareFigures {
  topPercent: number | null;
  absoluteTopPercent: number | null;
  lostTopToBudgetPercent: number | null;
  lostTopToRankPercent: number | null;
  includesUnderTenPercent: boolean;
  topUnderTen: boolean;
  absoluteTopUnderTen: boolean;
}
export interface CampaignCalls {
  name: string;
  spend: number | null;
  calls: number;
  callsAtLeast30s: number | null;
  costPerCallAtLeast30s: number | null;
  impressionSharePercent: number | null;
  shareOfSpendPercent: number | null;
}
export interface RoleFigures {
  spend: number | null;
  shareOfLocationSpendPercent: number | null;
  calls: number | null;
  callsAtLeast30s: number | null;
}
export interface TermRow {
  term: string;
  spend: number | null;
  clicks: number | null;
  conversions: number | null;
  /** The campaign(s) the term ran in, the largest spender first. */
  campaigns: string[];
  /** Run by the competitor campaign on purpose: expected spend, never "worth a look". */
  expected: boolean;
  /** "your name" for a search for the practice itself, "expected" for a competitor term: the table's tag. */
  tag: 'your name' | 'expected' | null;
}
export interface ChangeRow {
  date: string;
  time: string | null;
  campaign: string;
  what: string;
  from: string | null;
  to: string | null;
  by: string | null;
  before: WindowFigures | null;
  after: WindowFigures | null;
  /** Shown on the page. Jenny's read holds every line; the page shows the first by rank. */
  onPage: boolean;
}
export interface WindowFigures {
  from: string | null;
  to: string | null;
  spend: number | null;
  clicks: number | null;
  callsAtLeast30s: number | null;
}

/** Whether a comparison with the earlier window is like-for-like, and why not. */
export interface Comparability {
  likeForLike: boolean;
  why: string[];
}

export interface SnapshotView {
  header: {
    /** "Little Heroes Pediatric Dentistry, Anderson": the practice once, then the location. */
    title: string;
    location: string;
    practice: string | null;
    windowStart: string | null;
    windowEnd: string | null;
    asOf: string | null;
    /** "Oct 30, 1:31 PM EDT": the practice's own clock, labelled. */
    asOfLocal: string | null;
    campaignsIncluded: string[];
    /** The campaigns counted, by platform, with the shared practice/location prefix and location tags taken off. */
    campaigns: Array<{ platform: string; names: string[] }>;
    /** One status per platform: synced when, last sync failed, or why it is not in the report. */
    platformStatus: Array<{ label: string; status: string }>;
    /** Meta campaigns counted here whose names give no location: "location not determined". */
    locationNotDetermined: string[];
    /** The saved time zone is not the address's: call times and office-hours figures are off by hours. */
    timezoneWarning: string | null;
    notReady: Array<{ label: string; reason: string }>;
  };
  /** The location's 30-day spend and what each call of 30s or more cost: the report's headline tiles. */
  totals30: { spend: number | null; costPerCallAtLeast30s: number | null } | null;
  /** When the Google ads run, from each campaign's ad schedule: "Mon–Fri 8 AM to 5 PM", or null when unknown. */
  adsRun: string | null;
  /** Days the ad schedule runs past the office hours, and where the hours are set. */
  officeHours: { differsFromAdSchedule: string[]; source: string | null } | null;
  /** Platforms whose last sync failed: the figures are from their last successful sync. */
  stale: Array<{ label: string; lastSyncAt: string | null }>;
  comparability: { prior30d: Comparability | null; prior7d: Comparability | null };
  thisWeek: { last7d: WeekFigures; prior7d: WeekFigures } | null;
  pacing: Section<{
    monthToDateSpend: number | null;
    approvedMonthlyBudget: number | null;
    runRateProjection: number | null;
    projectionPercentOfBudget: number | null;
    monthToDatePercentOfBudget: number | null;
  }>;
  reach: {
    /** Google Search's own times shown and clicks, next to its shares; Meta's apart, never added in. */
    impressions: number | null;
    clicks: number | null;
    meta: { impressions: number | null; clicks: number | null } | null;
    otherGoogle: { impressions: number | null; clicks: number | null } | null;
    share: Section<ShareFigures>;
    topOfPage: Section<TopShareFigures>;
    sumCheck: string[];
  } | null;
  calls: Section<{
    total: number | null;
    answered: number | null;
    missed: number | null;
    atLeast30s: number | null;
    atLeast60s: number | null;
    medianDurationSeconds: number | null;
    missedCalls: Array<{ date: string; time: string | null; campaign: string | null }>;
    outsideOfficeHoursPercent: number | null;
  }>;
  campaignCalls: CampaignCalls[];
  conversions: Section<{
    conversions: number | null;
    allConversions: number | null;
    actions: Array<{
      name: string;
      countedIn: string | null;
      conversions: number | null;
      allConversions: number | null;
    }>;
    reconciliation: string[];
  }>;
  brand: Section<{
    terms: string[];
    branded: RoleFigures;
    everythingElse: RoleFigures;
    /** Meta's own row: spend and clicks, never calls (Meta has no call tracking). */
    meta: { spend: number | null; clicks: number | null } | null;
    /** No campaign is brand-only: where searches for the practice's own name ran instead. */
    noBrandOnlyCampaign: boolean;
    brandSearchesByCampaign: Array<{ campaign: string; spend: number | null; terms: string[] }>;
  }>;
  /** `googleStatus`: whether Google's campaign statuses were read; when not, nothing is said about serving. */
  health: {
    googleStatus: Section<{ readOn: string | null }>;
    /** Google's own word on its statuses, as Google's ("Google lists every campaign as eligible..."). */
    googleView: string | null;
    /** Every enabled Google campaign that showed to no one in the window, whatever its status says. */
    onButNoImpressions: Array<{
      campaign: string;
      dailyBudget: number | null;
      schedule: string | null;
      googleStatus: string | null;
      reason: string | null;
    }>;
    notServing: Array<{ campaign: string; reasons: string[] }>;
    limitedByBudget: string[];
    disapproved: Array<{ campaign: string; reasons: string[] }>;
    learning: string[];
    /** Paused or removed with nothing in the window, with disapproved ads: listed apart, never a warning. */
    inactiveWithDisapprovedAds: string[];
  } | null;
  searchTerms: Section<{
    top: TermRow[];
    worthALook: TermRow[];
    worthALookCount: number | null;
    worthALookSpend: number | null;
    /** Searches the competitor campaign bid on by design. */
    competitorCampaignTerms: {
      termCount: number | null;
      spend: number | null;
      top: string[];
    } | null;
    /** Spend in the window the search-term report does not list by search. */
    spendNotItemized: number | null;
    spendOnAllTerms: number | null;
    namingOtherLocations: {
      places: string[];
      spend: number | null;
      termCount: number | null;
      topTerms: string[];
    } | null;
  }>;
  whatChanged: Section<{
    changes: ChangeRow[];
    changesInWindow: number | null;
    linesShown: string | null;
    partial: string | null;
    /** The zone the change times are in, as the practice reads it ("EDT"). */
    timeZone: string | null;
    /** Campaigns changed lately: no further change is recommended for each until its date. */
    cooldowns: Array<{
      campaign: string;
      lastChange: string | null;
      until: string | null;
      by: string | null;
    }>;
    /** Changes PracticeNova made in Google Ads on the owner's approval, with what they came from. */
    throughPracticeNova: Array<{
      date: string | null;
      change: string;
      cameFrom: string | null;
      cooldownUntil: string | null;
      undone: boolean;
    }>;
  }>;
  outcomes: Section<{
    periodStart: string | null;
    periodEnd: string | null;
    byCampaign: Array<{
      campaign: string;
      newPatients: number | null;
      costPerNewPatient: number | null;
    }>;
    totalNewPatients: number | null;
    costPerNewPatient: number | null;
  }>;
  worthAttention: Array<{ fact: string; why: string | null }>;
  boundary: string | null;
}

const week = (v: unknown): WeekFigures => {
  const r = rec(v);
  return {
    spend: num(r.spend),
    calls: num(r.calls),
    callsAtLeast30s: num(r.callsAtLeast30s),
    missed: num(r.missed),
  };
};

const roleFigures = (v: unknown): RoleFigures => {
  const r = rec(v);
  return {
    spend: num(r.spend),
    // Of Google spend (the split is Google's); an older snapshot gave it of all spend.
    shareOfLocationSpendPercent:
      num(r.shareOfGoogleSpendPercent) ?? num(r.shareOfLocationSpendPercent),
    calls: num(r.calls),
    callsAtLeast30s: num(r.callsAtLeast30s),
  };
};

const term = (v: unknown): TermRow | null => {
  const r = rec(v);
  const t = str(r.term);
  return t
    ? {
        term: t,
        spend: num(r.spend),
        clicks: num(r.clicks),
        conversions: num(r.conversions),
        campaigns: strings(r.campaigns),
        expected: typeof r.expected === 'string',
        tag:
          r.expected === 'brand search'
            ? 'your name'
            : typeof r.expected === 'string'
              ? 'expected'
              : null,
      }
    : null;
};

const comparability = (v: unknown): Comparability | null => {
  if (!isRecord(v) || typeof v.likeForLike !== 'boolean') return null;
  return { likeForLike: v.likeForLike, why: strings(v.why).map(readableDates) };
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "calls are held only from 2026-09-30" (a snapshot from before dates were worded) -> "... from Sep 30". */
export function readableDates(text: string): string {
  return text.replace(/\b(20\d{2})-(\d{2})-(\d{2})\b/g, (whole, _y, m, d) =>
    MONTHS[Number(m) - 1] ? `${MONTHS[Number(m) - 1]} ${Number(d)}` : whole,
  );
}

const TAG = /\s*[[(][A-Za-z]{2,4}[\])]\s*/g;
/**
 * The report's title, the practice named once: "Little Heroes Pediatric Dentistry, Anderson". A location
 * named like its practice ("Little Heroes Pediatric Dentistry [AN]") is named by its city instead.
 */
export function reportTitle(
  location: string,
  practice: string | null,
  city: string | null,
): string {
  const clean = (s: string) => s.replace(TAG, ' ').replace(/\s+/g, ' ').trim();
  const practiceName = practice ? clean(practice) : '';
  const place = clean(location);
  if (!practiceName) return place;
  // "Mt Diablo Family Dentists" is "Mt. Diablo Family Dentists": compared without punctuation or case.
  const bare = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '');
  const where = place && bare(place) !== bare(practiceName) ? place : (city?.trim() ?? '');
  return where ? `${practiceName}, ${where}` : practiceName;
}

const windowFigures = (v: unknown): WindowFigures | null => {
  if (!isRecord(v)) return null;
  return {
    from: str(v.from),
    to: str(v.to),
    spend: num(v.spend),
    clicks: num(v.clicks),
    callsAtLeast30s: num(v.callsAtLeast30s),
  };
};

/** A change value as words: a budget's dollars, a status, or the fields that changed. */
export function changeValue(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (Array.isArray(v))
    return (
      v
        .map((x) => changeValue(x))
        .filter(Boolean)
        .join(', ') || null
    );
  if (!isRecord(v)) return null;
  const parts = Object.entries(v).flatMap(([key, value]) => {
    const inner = changeValue(value);
    if (inner === null) return [];
    const label = key.replace(/_/g, ' ');
    return key === 'amount' && typeof value === 'number'
      ? [`$${value.toFixed(2)}`]
      : [`${label}: ${inner}`];
  });
  return parts.length > 0 ? parts.join('; ') : null;
}

// "Little Heroes - Anderson - Branded [AN]" -> "Branded": the shared prefix and the location tag say nothing here.
const LOCATION_TAG = /\s*[[(][A-Z]{2,3}[\])]\s*/g;
const SEPARATOR = /\s+[-–|:/]\s+/g;

/** Campaign names without what every one of them repeats (practice, location, a location tag like "[AN]"). */
export function shortCampaignNames(names: string[]): string[] {
  const tagless = names.map((n) => n.replace(LOCATION_TAG, ' ').replace(/\s+/g, ' ').trim());
  if (tagless.length < 2) return tagless;
  let shared = tagless[0];
  for (const name of tagless.slice(1)) {
    let i = 0;
    while (i < shared.length && i < name.length && shared[i] === name[i]) i += 1;
    shared = shared.slice(0, i);
  }
  let cut = 0;
  for (const match of shared.matchAll(SEPARATOR)) cut = (match.index ?? 0) + match[0].length;
  return cut === 0 || tagless.some((n) => n.slice(cut).trim() === '')
    ? tagless
    : tagless.map((n) => n.slice(cut));
}

/** "Name (Google)" entries, grouped by platform, short names. */
export function campaignsByPlatform(
  included: string[],
): Array<{ platform: string; names: string[] }> {
  const groups = new Map<string, string[]>();
  for (const entry of included) {
    const match = /^(.*) \((Google|Meta)\)$/.exec(entry);
    const platform = match ? match[2] : 'Other';
    groups.set(platform, [...(groups.get(platform) ?? []), match ? match[1] : entry]);
  }
  return [...groups.entries()].map(([platform, names]) => ({
    platform,
    names: shortCampaignNames(names),
  }));
}

/** "07:00–17:00" (an older snapshot's schedule) as the practice says it: "7 AM to 5 PM". */
export function clockRange(text: string): string {
  const word = (h: string, m: string) => {
    const hour = Number(h);
    return `${hour % 12 === 0 ? 12 : hour % 12}${m === '00' ? '' : `:${m}`} ${hour < 12 || hour === 24 ? (hour === 24 ? 'midnight' : 'AM') : 'PM'}`.replace(
      '12 midnight',
      'midnight',
    );
  };
  return text.replace(
    /\b(\d{2}):(\d{2})[–-](\d{2}):(\d{2})\b/g,
    (_all, h1, m1, h2, m2) => `${word(h1, m1)} to ${word(h2, m2)}`,
  );
}

/** When the spending Google campaigns run: one schedule when they share it, else each schedule once. */
export function adsRunWords(schedules: Array<string | null>): string | null {
  const known = [...new Set(schedules.filter((s): s is string => s !== null).map(clockRange))];
  if (known.length === 0 || known.length !== new Set(schedules).size) return null;
  return known.length === 1 ? known[0] : null;
}

export function snapshotView(raw: unknown): SnapshotView | null {
  if (!isRecord(raw)) return null;
  const location = rec(raw.location);
  const name = str(location.name);
  if (!name) return null;
  const windows = rec(raw.windows);
  const last30 = rec(windows.last30d);
  const platforms = Array.isArray(raw.platforms) ? raw.platforms.map(rec) : [];

  const thisWeek = isRecord(raw.thisWeek)
    ? { last7d: week(raw.thisWeek.last7d), prior7d: week(raw.thisWeek.prior7d) }
    : null;
  const plan = rec(raw.plan);
  const reachRaw = rec(rec(raw.reach).last30d);
  const campaignRows = tableRows(raw.campaigns);

  return {
    header: {
      title: reportTitle(name, str(location.practice), str(location.city)),
      location: name,
      practice: str(location.practice),
      windowStart: str(last30.start),
      windowEnd: str(last30.end),
      asOf: str(raw.asOf),
      asOfLocal: str(raw.asOfLocal),
      campaignsIncluded: strings(raw.campaignsIncluded),
      campaigns: campaignsByPlatform(strings(raw.campaignsIncluded)),
      locationNotDetermined: strings(rec(raw.locationNotDetermined).campaigns),
      timezoneWarning: str(rec(location.timezoneMismatch).warning),
      platformStatus: platforms.map((p) => ({
        label: str(p.label) ?? str(p.platform) ?? 'A platform',
        status:
          p.state === 'ready'
            ? str(p.lastSyncLocal)
              ? `synced ${str(p.lastSyncLocal)}`
              : 'up to date'
            : p.state === 'sync_failed'
              ? `last sync failed; figures from ${str(p.lastSyncLocal) ?? 'the last good sync'}`
              : `not in this report: ${str(p.reason) ?? 'not ready'}`,
      })),
      // A failed sync still has figures from the last good one: that is the stale banner, not "not in this report".
      notReady: platforms
        .filter((p) => p.state !== 'ready' && p.state !== 'sync_failed')
        .map((p) => ({
          label: str(p.label) ?? str(p.platform) ?? 'A platform',
          reason: str(p.reason) ?? 'not ready',
        })),
    },
    stale: platforms
      .filter((p) => p.state === 'sync_failed')
      .map((p) => ({
        label: str(p.label) ?? str(p.platform) ?? 'A platform',
        lastSyncAt: str(p.lastSyncAt),
      })),
    comparability: {
      prior30d: comparability(rec(raw.comparability).prior30d),
      prior7d: comparability(rec(raw.comparability).prior7d),
    },
    thisWeek,
    totals30: isRecord(rec(raw.totals).last30d)
      ? {
          spend: num(rec(rec(raw.totals).last30d).spend),
          costPerCallAtLeast30s: num(rec(rec(raw.totals).last30d).costPerCallAtLeast30s),
        }
      : null,
    adsRun: adsRunWords(
      campaignRows
        .filter((c) => c.platform === 'google' && (num(c.spend) ?? 0) > 0)
        .map((c) => str(c.schedule)),
    ),
    officeHours:
      isRecord(plan.officeHours) && plan.officeHours.available === true
        ? {
            differsFromAdSchedule: strings(plan.officeHours.differsFromAdSchedule),
            source: str(plan.officeHours.source),
          }
        : null,
    pacing: section(plan.pacing, (p) => ({
      monthToDateSpend: num(p.monthToDateSpend),
      approvedMonthlyBudget: num(p.approvedMonthlyBudget),
      runRateProjection: num(p.runRateProjection),
      projectionPercentOfBudget: num(p.projectionPercentOfBudget),
      monthToDatePercentOfBudget: num(p.monthToDatePercentOfBudget),
    })),
    reach: isRecord(rec(raw.reach).last30d)
      ? {
          impressions: num(reachRaw.impressions),
          clicks: num(reachRaw.clicks),
          meta: isRecord(reachRaw.meta)
            ? { impressions: num(reachRaw.meta.impressions), clicks: num(reachRaw.meta.clicks) }
            : null,
          otherGoogle: isRecord(reachRaw.otherGoogle)
            ? {
                impressions: num(reachRaw.otherGoogle.impressions),
                clicks: num(reachRaw.otherGoogle.clicks),
              }
            : null,
          share: section(reachRaw.impressionShare, (s) => ({
            impressionSharePercent: num(s.impressionSharePercent),
            lostToBudgetPercent: num(s.lostToBudgetPercent),
            lostToRankPercent: num(s.lostToRankPercent),
            includesUnderTenPercent:
              s.includesUnderTenPercent === true || typeof s.underTenPercent === 'string',
            shownUnderTen: s.impressionShareUnderTen === true,
          })),
          topOfPage: section(reachRaw.topOfPage, (t) => ({
            topPercent: num(t.topImpressionSharePercent),
            absoluteTopPercent: num(t.absoluteTopImpressionSharePercent),
            lostTopToBudgetPercent: num(t.lostTopToBudgetPercent),
            lostTopToRankPercent: num(t.lostTopToRankPercent),
            includesUnderTenPercent: typeof t.underTenPercent === 'string',
            topUnderTen: t.topImpressionShareUnderTen === true,
            absoluteTopUnderTen: t.absoluteTopImpressionShareUnderTen === true,
          })),
          // Where shown + lost to budget + lost to rank is not 100%, and why.
          sumCheck: strings(rec(rec(raw.reach).sumCheck).lines),
        }
      : null,
    calls: section(raw.calls, (c) => {
      const l = rec(c.last30d);
      const hours = rec(c.officeHours);
      return {
        total: num(l.total),
        answered: num(l.answered),
        missed: num(l.missed),
        atLeast30s: num(l.atLeast30s),
        atLeast60s: num(l.atLeast60s),
        medianDurationSeconds: num(l.medianDurationSeconds),
        missedCalls: (Array.isArray(c.missedCalls) ? c.missedCalls : [])
          .flatMap((m) => {
            const d = str(rec(m).date);
            return d ? [{ date: d, time: str(rec(m).time), campaign: str(rec(m).campaign) }] : [];
          })
          // Oldest first, whatever order an older snapshot kept them in.
          .sort((a, b) => `${a.date} ${a.time ?? ''}`.localeCompare(`${b.date} ${b.time ?? ''}`)),
        outsideOfficeHoursPercent: hours.available === true ? num(hours.outsidePercent) : null,
      };
    }),
    campaignCalls: campaignRows
      .filter((c) => c.platform === 'google' && typeof c.name === 'string')
      .map((c) => ({
        name: c.name as string,
        spend: num(c.spend),
        calls: num(c.calls) ?? 0,
        callsAtLeast30s: num(c.callsAtLeast30s),
        costPerCallAtLeast30s: num(c.costPerCallAtLeast30s),
        impressionSharePercent: num(c.impressionSharePercent),
        shareOfSpendPercent: num(c.shareOfLocationSpendPercent),
      })),
    conversions: section(raw.conversions, (c) => ({
      conversions: num(c.conversions),
      allConversions: num(c.allConversions),
      actions: tableRows(c.actions).flatMap((a) =>
        typeof a.name === 'string'
          ? [
              {
                name: a.name,
                countedIn: str(a.countedIn),
                conversions: num(a.conversions),
                allConversions: num(a.allConversions),
              },
            ]
          : [],
      ),
      reconciliation: strings(c.reconciliation),
    })),
    brand: section(raw.brand, (b) => ({
      terms: strings(rec(b.brandTerms).terms),
      branded: roleFigures(b.branded),
      everythingElse: roleFigures(b.everythingElse),
      meta: isRecord(b.meta) ? { spend: num(b.meta.spend), clicks: num(b.meta.clicks) } : null,
      noBrandOnlyCampaign: b.noBrandOnlyCampaign === true,
      brandSearchesByCampaign: (Array.isArray(b.brandSearchesByCampaign)
        ? b.brandSearchesByCampaign
        : []
      ).flatMap((x) => {
        const c = str(rec(x).campaign);
        return c ? [{ campaign: c, spend: num(rec(x).spend), terms: strings(rec(x).terms) }] : [];
      }),
    })),
    health:
      isRecord(raw.health) && raw.health.available === true
        ? {
            googleStatus: section(raw.health.googleStatus, (g) => ({ readOn: str(g.readOn) })),
            googleView: str(raw.health.googleView),
            onButNoImpressions: (Array.isArray(raw.health.onButNoImpressions)
              ? raw.health.onButNoImpressions
              : []
            ).flatMap((x) => {
              const c = str(rec(x).campaign);
              return c
                ? [
                    {
                      campaign: c,
                      dailyBudget: num(rec(x).dailyBudget),
                      schedule: str(rec(x).schedule),
                      googleStatus: str(rec(x).googleStatus),
                      reason: str(rec(x).reason),
                    },
                  ]
                : [];
            }),
            notServing: (Array.isArray(raw.health.notServing) ? raw.health.notServing : []).flatMap(
              (x) => {
                const c = str(rec(x).campaign);
                return c ? [{ campaign: c, reasons: strings(rec(x).reasons) }] : [];
              },
            ),
            limitedByBudget: (Array.isArray(raw.health.limitedByBudget)
              ? raw.health.limitedByBudget
              : []
            ).flatMap((x) => str(rec(x).campaign) ?? []),
            disapproved: (Array.isArray(raw.health.disapproved)
              ? raw.health.disapproved
              : []
            ).flatMap((x) => {
              const c = str(rec(x).campaign);
              return c ? [{ campaign: c, reasons: strings(rec(x).reasons) }] : [];
            }),
            learning: (Array.isArray(raw.health.learning) ? raw.health.learning : []).flatMap(
              (x) => str(rec(x).campaign) ?? [],
            ),
            inactiveWithDisapprovedAds: (Array.isArray(
              raw.health.inactiveCampaignsWithDisapprovedAds,
            )
              ? raw.health.inactiveCampaignsWithDisapprovedAds
              : []
            ).flatMap((x) => str(rec(x).campaign) ?? []),
          }
        : null,
    searchTerms: section(raw.searchTerms, (s) => ({
      top: (Array.isArray(s.top) ? s.top : []).flatMap((t) => term(t) ?? []),
      worthALook: (Array.isArray(s.worthALook) ? s.worthALook : []).flatMap((t) => term(t) ?? []),
      worthALookCount: num(s.worthALookCount),
      worthALookSpend: num(s.worthALookSpend),
      competitorCampaignTerms: isRecord(s.competitorCampaignTerms)
        ? {
            termCount: num(s.competitorCampaignTerms.termCount),
            spend: num(s.competitorCampaignTerms.spend),
            top: (Array.isArray(s.competitorCampaignTerms.top)
              ? s.competitorCampaignTerms.top
              : []
            ).flatMap((t) => str(rec(t).term) ?? []),
          }
        : null,
      spendNotItemized: isRecord(s.spendNotItemized) ? num(s.spendNotItemized.spend) : null,
      spendOnAllTerms: num(s.spendOnAllTerms),
      namingOtherLocations:
        isRecord(s.namingOtherLocations) && s.namingOtherLocations.available === true
          ? {
              places: strings(s.namingOtherLocations.places),
              spend: num(s.namingOtherLocations.spend),
              termCount: num(s.namingOtherLocations.termCount),
              topTerms: (Array.isArray(s.namingOtherLocations.topTerms)
                ? s.namingOtherLocations.topTerms
                : []
              ).flatMap((t) => str(rec(t).term) ?? []),
            }
          : null,
    })),
    whatChanged: section(raw.whatChanged, (w) => ({
      changes: tableRows(w.changes).flatMap((c) =>
        typeof c.date === 'string'
          ? [
              {
                date: c.date,
                time: str(c.time),
                campaign: str(c.campaign) ?? '',
                what: str(c.what) ?? 'a setting',
                from: changeValue(c.from),
                to: changeValue(c.to),
                by: str(c.by),
                before: windowFigures(c.before7d),
                after: windowFigures(c.after7d),
                // A snapshot from before the read held every line: each of its lines is a page line.
                onPage: c.onPage !== false,
              },
            ]
          : [],
      ),
      changesInWindow: num(w.changesInWindow),
      linesShown: str(w.linesShown),
      partial: str(w.partial),
      timeZone: str(w.timeZone),
      cooldowns: (Array.isArray(w.cooldowns) ? w.cooldowns : []).flatMap((x) => {
        const campaign = str(rec(x).campaign);
        return campaign
          ? [
              {
                campaign,
                lastChange: str(rec(x).lastChange),
                until: str(rec(x).until),
                by: str(rec(x).by),
              },
            ]
          : [];
      }),
      throughPracticeNova: (Array.isArray(w.throughPracticeNova)
        ? w.throughPracticeNova
        : []
      ).flatMap((x) => {
        const change = str(rec(x).change);
        return change
          ? [
              {
                date: str(rec(x).date),
                change,
                cameFrom: str(rec(x).cameFrom),
                cooldownUntil: str(rec(x).cooldownUntil),
                undone: rec(x).undone === true,
              },
            ]
          : [];
      }),
    })),
    outcomes: section(raw.outcomes, (o) => {
      const period = rec(o.period);
      const total = rec(o.total);
      return {
        periodStart: str(period.start),
        periodEnd: str(period.end),
        byCampaign: (Array.isArray(o.byCampaign) ? o.byCampaign : []).flatMap((x) => {
          const c = str(rec(x).campaign);
          return c
            ? [
                {
                  campaign: c,
                  newPatients: num(rec(x).newPatients),
                  costPerNewPatient: num(rec(x).costPerNewPatient),
                },
              ]
            : [];
        }),
        totalNewPatients: num(total.newPatients),
        costPerNewPatient: num(total.costPerNewPatient),
      };
    }),
    worthAttention: (Array.isArray(raw.worthAttention) ? raw.worthAttention : []).flatMap((f) => {
      const fact = str(rec(f).fact);
      return fact ? [{ fact, why: str(rec(f).why) }] : [];
    }),
    boundary: str(raw.boundary),
  };
}

/**
 * One section of the agent's written report, by its heading ("## Jenny's take"): the text up to the next heading,
 * or null when the report has no such section. The page draws every section of figures from the snapshot; the
 * agent writes only its take, the worth-attention facts and where the ad data ends.
 */
export function writtenSection(body: string | null | undefined, heading: string): string | null {
  if (!body) return null;
  const lines = body.split('\n');
  const start = lines.findIndex(
    (line) =>
      line
        .replace(/^#+\s*/, '')
        .trim()
        .toLowerCase() === heading.toLowerCase() && /^#+\s/.test(line),
  );
  if (start < 0) return null;
  const end = lines.findIndex((line, i) => i > start && /^#+\s/.test(line));
  const text = lines
    .slice(start + 1, end < 0 ? undefined : end)
    .join('\n')
    .trim();
  return text || null;
}

/**
 * Missed calls as the page lists them: calls in the same hour and campaign once, with a count ("Oct 26,
 * 3:00 PM, 2 calls (Google Build)"), oldest first. Calls are held to the hour, so two in one hour read alike.
 */
export function missedCallLines(
  missed: ReadonlyArray<{ date: string; time: string | null; campaign: string | null }>,
  label: (m: { date: string; time: string | null }) => string,
  shortName: (campaign: string) => string = (c) => c,
): string[] {
  const groups = new Map<string, { at: string; campaign: string | null; count: number }>();
  for (const m of missed) {
    const key = `${m.date} ${m.time ?? ''}|${m.campaign ?? ''}`;
    const g = groups.get(key) ?? { at: label(m), campaign: m.campaign, count: 0 };
    g.count += 1;
    groups.set(key, g);
  }
  // Oldest first, by date and then minutes since midnight: "9 PM" and "10 AM" do not sort as text.
  const minutes = (time: string): number => {
    const m = /^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/i.exec(time.trim());
    if (!m) return 0;
    const h = (Number(m[1]) % (m[3] ? 12 : 24)) + (m[3]?.toUpperCase() === 'PM' ? 12 : 0);
    return h * 60 + Number(m[2] ?? 0);
  };
  const order = (key: string) => {
    const [at] = key.split('|');
    const [date, ...time] = (at ?? '').split(' ');
    return `${date} ${String(minutes(time.join(' '))).padStart(4, '0')}`;
  };
  return [...groups.entries()]
    .sort(([a], [b]) => order(a).localeCompare(order(b)))
    .map(
      ([, g]) =>
        `${g.at}${g.count > 1 ? `, ${g.count} calls` : ''}${g.campaign ? ` (${shortName(g.campaign)})` : ''}`,
    );
}
